"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMetadata = void 0;
const wayback_items_1 = require("../wayback-items");
const config_1 = require("./config");
// can only get metadata when the map is between the min and max zoom level (10 <= mapZoom <= 23)
const MAX_ZOOM = 23;
const MIN_ZOOM = 10;
const { SOURCE_DATE, SOURCE_PROVIDER, SOURCE_NAME, RESOLUTION, ACCURACY } = config_1.METADATA_FIELD_NAMES;
/**
 * Query the wayback metadata feature service associated with the given wayback release
 * to retrieve information (acquisition date, provider, resolution, etc.) of the world imagery wayback tile image
 * at the specified location and zoom level.
 *
 * @param point  The geographic coordinates (longitude and latitude) representing the location to be used in the query. (e.g., `{longitude: -100.05, latitude: 35.10}`)
 * @param zoom The zoom level for the imagery.
 * @param releaseNumber The world imagery wayback release number.
 *
 * @returns A Promise that resolves to `WaybackMetadata`, containing metadata information like date, provider,
 * source, resolution, and accuracy for the specified location and zoom level.
 */
const getMetadata = (point, zoom, releaseNumber) => __awaiter(void 0, void 0, void 0, function* () {
    if (!point || !zoom || !releaseNumber) {
        throw new Error('Failed to query metadata because the required parameters are missing');
    }
    const { longitude, latitude } = point;
    const queryParams = new URLSearchParams({
        f: 'json',
        where: '1=1',
        outFields: [
            SOURCE_DATE,
            SOURCE_PROVIDER,
            SOURCE_NAME,
            RESOLUTION,
            ACCURACY,
        ].join(','),
        geometry: JSON.stringify({
            spatialReference: { wkid: 4326 },
            x: longitude,
            y: latitude,
        }),
        returnGeometry: 'false',
        geometryType: 'esriGeometryPoint',
        spatialRel: 'esriSpatialRelIntersects',
    });
    // Gets the query URL using the provided release number and zoom level
    const requestURL = yield getQueryUrl(releaseNumber, zoom);
    const res = yield fetch(`${requestURL}?${queryParams.toString()}`);
    if (!res.ok) {
        throw new Error('failed to query metadata for ' +
            releaseNumber +
            ' release of world imagery wayback');
    }
    const data = (yield res.json());
    if (data.error) {
        throw data.error;
    }
    const feature = data.features && data.features.length ? data.features[0] : null;
    if (!feature) {
        return null;
    }
    const { attributes } = feature;
    const date = attributes[SOURCE_DATE];
    const provider = attributes[SOURCE_PROVIDER];
    const source = attributes[SOURCE_NAME];
    const resolution = attributes[RESOLUTION];
    const accuracy = attributes[ACCURACY];
    return {
        date,
        provider,
        source,
        resolution,
        accuracy,
    };
});
exports.getMetadata = getMetadata;
/**
 * Get the URL that will be used to query the wayback metadata feature service. For each wayback release,
 * there will be a metadata layer created for that release.
 *
 * This function helps to find the Metadata Layer using the input release number and the id of sublayer by zoom number.
 *
 * @param releaseNum The release number of the wayback data.
 * @param zoom The zoom number used to identify the sublayer.
 * @returns URL to be used to do a query of the metadata layer.
 */
const getQueryUrl = (releaseNum, zoom) => __awaiter(void 0, void 0, void 0, function* () {
    const waybackItem = yield (0, wayback_items_1.getWaybackItemByReleaseNumber)(releaseNum);
    if (!waybackItem) {
        throw new Error(`failed to find wayback item that with release number of ${releaseNum}`);
    }
    const { metadataLayerUrl } = waybackItem;
    const layerId = getLayerId(zoom);
    return `${metadataLayerUrl}/${layerId}/query`;
});
const getLayerId = (zoom) => {
    // the metadata service has 14 sub layers (0-13) that provide metadata for imagery tiles from zoom level 23 (layer 0) up to zoom level 10 (layer 13)
    const layerID = MAX_ZOOM - zoom;
    // id of the metadata layer for the imagery tiles at zoom level 10,
    // in other words, the imagery tile that is with the biggest resolution (e.g., 150m resolution)
    const layerIdForMinZoom = MAX_ZOOM - MIN_ZOOM;
    if (layerID > layerIdForMinZoom) {
        return layerIdForMinZoom;
    }
    return layerID;
};
//# sourceMappingURL=index.js.map